if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.prevButton = this.querySelector('[data-modal-nav="prev"]');
        this.nextButton = this.querySelector('[data-modal-nav="next"]');
        this.zoomInButton = this.querySelector('[data-modal-zoom-in]');
        this.zoomOutButton = this.querySelector('[data-modal-zoom-out]');
        this.rotateButton = this.querySelector('[data-modal-rotate]');
        this.panzoom = null;
        this.rotation = 0; // degrees; reset per image in activateMedia()

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.showAdjacentMedia(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.showAdjacentMedia(1));
        if (this.zoomInButton) this.zoomInButton.addEventListener('click', () => this.panzoom && this.panzoom.zoomIn());
        if (this.zoomOutButton) this.zoomOutButton.addEventListener('click', () => this.panzoom && this.panzoom.zoomOut());
        if (this.rotateButton) this.rotateButton.addEventListener('click', () => this.rotateActiveImage());

        // Arrow keys browse photos while the modal is open — keyboard parity
        // with the prev/next buttons and swipe/pinch (a11y).
        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showAdjacentMedia(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showAdjacentMedia(1);
          }
        });
      }

      hide() {
        this.teardownPanzoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      showAdjacentMedia(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return;
        const currentIndex = items.findIndex((item) => item.classList.contains('active'));
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        this.activateMedia(items[nextIndex]);
      }

      showActiveMedia() {
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        this.activateMedia(activeMedia);
      }

      activateMedia(activeMedia) {
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${activeMedia.getAttribute('data-media-id')}"])`).forEach(
          (element) => {
            element.classList.remove('active');
          }
        );
        this.teardownPanzoom();
        const isImage = activeMedia.nodeName === 'IMG';
        if (this.zoomInButton) this.zoomInButton.hidden = !isImage;
        if (this.zoomOutButton) this.zoomOutButton.hidden = !isImage;
        if (this.rotateButton) this.rotateButton.hidden = !isImage;
        this.rotation = 0;
        if (isImage) {
          activeMedia.style.rotate = '';
          this.fitImage(activeMedia);
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        this.updateCounter(activeMedia);

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // Some source photos are stored sideways (their real content — cover
      // text etc. — reads correctly only after a 90°/180° turn). Panzoom
      // itself has no rotate feature (pan/zoom only), so this is a small,
      // independent control: CSS's `rotate` is its own property (distinct
      // from `transform`, which Panzoom fully owns for scale/pan) since CSS
      // Transforms Level 2 — the two compose without either overwriting the
      // other.
      rotateActiveImage() {
        const activeImg = this.querySelector('.product-media-modal__content > img.active');
        if (!activeImg) return;
        if (this.panzoom) this.panzoom.zoom(1); // avoid an old zoomed/panned position surviving a size swap
        this.rotation = (this.rotation + 90) % 360;
        this.applyFit(activeImg);
      }

      updateCounter(activeMedia) {
        const currentEl = this.querySelector('[data-modal-counter-current]');
        if (!currentEl) return;
        const items = this.getMediaItems();
        const index = items.indexOf(activeMedia);
        if (index === -1) return;
        currentEl.textContent = index + 1;
      }

      // The true ceiling for "native size" isn't img.naturalWidth/Height —
      // those reflect whichever srcset candidate the browser happened to
      // load for the CURRENT layout width, which can itself change (the
      // browser may swap to a different candidate later, and re-evaluates
      // `sizes` against whatever width we just set — a moving target).
      // Shopify's srcset only ever lists candidates up to the source
      // image's real resolution, so the largest "...w" descriptor there is
      // a stable, load-independent stand-in for the true max resolution.
      // Height comes from the img's own width/height attributes, which
      // carry the correct aspect ratio regardless of which candidate is
      // currently loaded.
      getMaxNaturalSize(img) {
        let maxWidth = 0;
        (img.getAttribute('srcset') || '').split(',').forEach((candidate) => {
          const match = candidate.trim().match(/(\d+)w$/);
          if (match) maxWidth = Math.max(maxWidth, parseInt(match[1], 10));
        });
        if (!maxWidth) maxWidth = img.naturalWidth || img.width;
        const aspectRatio = img.width && img.height ? img.height / img.width : 1;
        return { width: maxWidth, height: Math.round(maxWidth * aspectRatio) };
      }

      fitImage(img) {
        if (img.complete) {
          this.applyFit(img);
        } else {
          img.addEventListener('load', () => this.applyFit(img), { once: true });
        }
      }

      applyFit(img) {
        const container = this.querySelector('.product-media-modal__content');
        const cs = getComputedStyle(container);
        const containerWidth = container.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const containerHeight = window.innerHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
        // A 90°/270° rotation swaps which screen axis the image's own width
        // and height land on — fit against the swapped available space so a
        // rotated portrait photo (now visually landscape) doesn't overflow
        // the screen sideways. Only for the fit-scale calculation below:
        // centering (further down) always centers against the container's
        // own, unrotated dimensions.
        let availWidth = containerWidth;
        let availHeight = containerHeight;
        if (this.rotation % 180 !== 0) [availWidth, availHeight] = [availHeight, availWidth];
        const { width: maxWidth, height: maxHeight } = this.getMaxNaturalSize(img);
        if (!maxWidth || !maxHeight) return;
        const fitScale = Math.min(1, availWidth / maxWidth, availHeight / maxHeight);
        const baseWidth = Math.round(maxWidth * fitScale);
        const baseHeight = Math.round(maxHeight * fitScale);
        img.style.width = `${baseWidth}px`;
        img.style.height = `${baseHeight}px`;
        // Center via real, explicit margins (not position:absolute +
        // top/left, and not the container's own flex align-items /
        // justify-content) — Panzoom computes where a click/pinch/wheel
        // point lands on the *image* by reading the image's actual margin
        // (see zoomToPoint in the vendored panzoom.min.js), on the
        // assumption that the image is positioned via margin offsets from
        // its parent's content box, same as normal block/flex flow. Both
        // position:absolute+top/left and flex auto-centering place the
        // image visually correctly but leave that margin at 0/auto, so
        // Panzoom's math silently read the wrong anchor point — the actual
        // root cause of zoom feeling like it wasn't zooming toward the
        // clicked/pinched point. Explicit margin (which can go negative,
        // same as the old top/left/negative-margin trick, for a rotated
        // image whose pre-rotation layout box is taller/wider than the
        // container) fixes that without losing centering: setting all four
        // sides — not just left/top — makes the margin box exactly fill
        // the container, so there's no free space left for the container's
        // own align-items/justify-content to additionally act on.
        const marginX = (containerWidth - baseWidth) / 2;
        const marginY = (containerHeight - baseHeight) / 2;
        img.style.position = '';
        img.style.top = '';
        img.style.left = '';
        img.style.marginLeft = img.style.marginRight = `${marginX}px`;
        img.style.marginTop = img.style.marginBottom = `${marginY}px`;
        img.style.rotate = this.rotation ? `${this.rotation}deg` : '';

        // How far Panzoom may zoom in before exceeding the image's true
        // resolution (1:1 native pixels) — never further than that.
        const maxScale = Math.max(1, maxWidth / baseWidth);
        this.setupPanzoom(img, maxScale, baseWidth, baseHeight, containerWidth, containerHeight);
      }

      // Replicates panzoom.min.js's zoomToPoint's focal-point formula (the
      // "effectiveArea" it also computes cancels out algebraically in that
      // formula and is omitted here). Reads the image's actual margin —
      // which applyFit() now sets as real, explicit margin rather than
      // position:absolute + top/left — since that's the box model
      // zoomToPoint's own math assumes for locating the image relative to
      // its parent.
      computeZoomFocal(clientX, clientY, scale, toScale) {
        const img = this._panzoomImg;
        const parent = img.parentElement;
        const parentRect = parent.getBoundingClientRect();
        const parentStyle = getComputedStyle(parent);
        const imgStyle = getComputedStyle(img);
        const imgRect = img.getBoundingClientRect();
        const originX =
          parentRect.left +
          parseFloat(parentStyle.paddingLeft) +
          parseFloat(parentStyle.borderLeftWidth) +
          parseFloat(imgStyle.marginLeft) +
          imgRect.width / scale / 2;
        const originY =
          parentRect.top +
          parseFloat(parentStyle.paddingTop) +
          parseFloat(parentStyle.borderTopWidth) +
          parseFloat(imgStyle.marginTop) +
          imgRect.height / scale / 2;
        return { x: (clientX - originX) * toScale, y: (clientY - originY) * toScale };
      }

      // Wheel to zoom (centered on the cursor), pinch/drag on touch, and
      // step-wise zoom in/out buttons — all handled by Panzoom rather than
      // hand-rolled gesture math (pinch-distance tracking, zoom-to-point
      // formulas all have plenty of edge cases). Panzoom's own `contain`
      // option is deliberately NOT used: it constrains scale relative to
      // the panzoom target's actual DOM parent (.product-media-modal__content),
      // which is close to viewport-sized — much bigger than the image's own
      // fitted box — so 'outside' forced an incorrect, too-high minimum
      // scale and 'inside' capped the maximum far too low. Panning is
      // clamped by hand instead (clampToBounds below), relative to the
      // image's own base size, which we already know exactly.
      //
      // The clamp is deliberately NOT applied on every 'panzoompan' event
      // (i.e. not live, mid-drag) — Panzoom's own drag handling computes
      // each move as `dragStartPan + (pointer delta)`, from a start
      // reference captured once at pointerdown and never updated again
      // during that same gesture. Forcing a corrected pan() mid-drag has no
      // lasting effect: the very next pointermove recomputes from that
      // same stale, uncorrected start reference and overwrites our
      // correction, which is exactly what showed up as the image
      // "snapping"/jumping during a drag. Zoom actions (wheel, pinch,
      // double-click, the +/- buttons) don't have this problem — each one
      // is a discrete call that reads the *current* pan position fresh, so
      // correcting right after is safe and sticks. So: clamp reactively on
      // 'panzoomzoom' (covers all zoom paths, including zoom-out, which
      // previously could leave the image visibly off-center at scale 1
      // because nothing re-clamped it after a zoom-only change), and
      // clamp once more, animated, on 'panzoomend' (drag release) to snap
      // back into bounds — the iOS-Photos-style rubber-band behavior.
      setupPanzoom(img, maxScale, baseWidth, baseHeight, containerWidth, containerHeight) {
        this.teardownPanzoom();
        if (typeof Panzoom === 'undefined') return; // CDN script still loading/blocked — plain fit-to-viewport still works, just without zoom
        this.panzoom = Panzoom(img, {
          maxScale,
          minScale: 1,
          cursor: '',
        });
        const clampToBounds = (detail, animate) => {
          const { x, y, scale } = detail;
          // Panzoom's pan offsets (x, y) live in screen space, but
          // baseWidth/baseHeight are the img's own pre-rotation dimensions.
          // At 90°/270° those two axes are swapped on screen, so the clamp
          // must swap them too — otherwise a rotated image can be panned
          // past its real edge on one axis while staying overclamped on
          // the other. containerWidth/containerHeight don't swap — the
          // container itself never rotates, it's already screen-aligned.
          const [screenWidth, screenHeight] =
            this.rotation % 180 !== 0 ? [baseHeight, baseWidth] : [baseWidth, baseHeight];
          // Panzoom's own transform is `scale(...) translate(...)` — scale
          // is the OUTER function, translate the INNER one — so a given x/y
          // pan value only ever moves the image on screen by x*scale /
          // y*scale px, not x / y px. The screen-space bound we actually
          // want is: the image may slide until its scaled edge reaches the
          // *container's* edge, never further — `screenWidth * scale -
          // containerWidth` is the total screen-space overhang (0 if the
          // scaled image is still narrower than the container, i.e. still
          // letterboxed at this zoom level — must not pan at all then).
          // Divided by 2 for one side, and by `scale` again to convert
          // that many screen pixels back to Panzoom's raw x/y units.
          // Using baseWidth/baseHeight here instead of containerWidth/
          // containerHeight (as an earlier version of this code did) is
          // only correct when the image exactly fills the container at
          // scale 1 — true for a container whose aspect ratio happens to
          // match the image's, false otherwise (the common case: most
          // images are letterboxed on one axis). That mismatch let panning
          // run out further than the image's real edge on the letterboxed
          // axis, then "restore" to a position that still didn't fully
          // re-cover the container — the reported "bounding box ignored
          // after zooming out" bug.
          const maxOffsetX = Math.max(0, screenWidth * scale - containerWidth) / (2 * scale);
          const maxOffsetY = Math.max(0, screenHeight * scale - containerHeight) / (2 * scale);
          const clampedX = Math.min(maxOffsetX, Math.max(-maxOffsetX, x));
          const clampedY = Math.min(maxOffsetY, Math.max(-maxOffsetY, y));
          if (clampedX !== x || clampedY !== y) this.panzoom.pan(clampedX, clampedY, { force: true, animate });
        };
        this._onWheel = (event) => this.panzoom.zoomWithWheel(event);
        this._onDblClick = (event) => {
          const currentScale = this.panzoom.getScale();
          const toScale = currentScale > 1 ? 1 : maxScale;
          // Not panzoom.zoomToPoint(): it's built for exactly this (zoom
          // centered on a client point, like a native photo viewer, rather
          // than always toward the image's own center), but it hard-codes
          // animate:false internally — losing the double-click's zoom
          // transition. So the small amount of focal-point math it does
          // (see zoomToPoint in the vendored panzoom.min.js) is replicated
          // here and fed into the public zoom(), which does respect
          // animate:true.
          const focal = this.computeZoomFocal(event.clientX, event.clientY, currentScale, toScale);
          this.panzoom.zoom(toScale, { focal, animate: true });
        };
        this._onPanzoomChange = (event) => {
          img.classList.toggle('is-zoomed', event.detail.scale > 1);
        };
        this._onPanzoomZoom = (event) => clampToBounds(event.detail, false);
        this._onPanzoomEnd = (event) => clampToBounds(event.detail, true);
        img.addEventListener('wheel', this._onWheel);
        img.addEventListener('dblclick', this._onDblClick);
        img.addEventListener('panzoomchange', this._onPanzoomChange);
        img.addEventListener('panzoomzoom', this._onPanzoomZoom);
        img.addEventListener('panzoomend', this._onPanzoomEnd);
        this._panzoomImg = img;
      }

      teardownPanzoom() {
        if (this.panzoom && this._panzoomImg) {
          this._panzoomImg.removeEventListener('wheel', this._onWheel);
          this._panzoomImg.removeEventListener('dblclick', this._onDblClick);
          this._panzoomImg.removeEventListener('panzoomchange', this._onPanzoomChange);
          this._panzoomImg.removeEventListener('panzoomzoom', this._onPanzoomZoom);
          this._panzoomImg.removeEventListener('panzoomend', this._onPanzoomEnd);
          this._panzoomImg.classList.remove('is-zoomed');
          this.panzoom.destroy();
        }
        this.panzoom = null;
        this._panzoomImg = null;
      }
    }
  );
}
